/**
 * THE CONTRACT EVERY DEVICE TAB IS BUILT AGAINST.
 *
 * The device page is one shell with several tabs, each in its own file. They are
 * written independently, so the shape they agree on lives here rather than in
 * any one of them — a tab that reaches into the shell, or into a sibling tab,
 * is the thing this file exists to prevent.
 *
 * WHERE THE DATA COMES FROM, and why the tabs never fetch the row themselves:
 * `buildFleetRows` already merges the device check-in, the hardware unit and
 * the resolved release target into one `FleetRow`. Re-deriving any of that per
 * tab would give slightly different answers about one device — which is
 * exactly the failure the merged row was written to end.
 *
 * PURITY: no React, no network. Helpers here are pure so a tab's test can call
 * them with no mocks.
 */

import type { DeviceHealthProblem, FleetCommand } from '@platform/shared-types';
import type { FleetRow } from '../fleet/fleetRows';
import type { DeviceModelRow } from '../../../services/hardwareApi';
import { deviceDisplayName } from '../../fleet/fleetStatus';

/**
 * The tabs, in the order they render (Fleet Monitoring redesign, approved
 * mockup `docs/mockups/fleet-monitoring-redesign-mockup.html` screen 2).
 * `?tab=` carries these verbatim. Danger zone is LAST and renders apart from
 * the rest — destructive actions never sit beside everyday ones.
 */
export const DEVICE_TAB_IDS = ['overview', 'logs', 'issues', 'health', 'updates', 'history', 'unit', 'danger'] as const;
export type DeviceTabId = (typeof DEVICE_TAB_IDS)[number];

/**
 * `unit` renders the INVENTORY unit page's own content rather than a copy of
 * it: photo, provisioning checklist, condition, damage, lifecycle timeline and
 * the order journey, all from `UnitPage`. One implementation shown in two
 * places is not duplication; a second implementation of the same thing is.
 */
export const DEVICE_TABS: { id: DeviceTabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'logs', label: 'Logs' },
  { id: 'issues', label: 'Issues' },
  { id: 'health', label: 'Health' },
  { id: 'updates', label: 'Updates & branding' },
  { id: 'history', label: 'History' },
  { id: 'unit', label: 'Unit' },
  { id: 'danger', label: 'Danger zone' },
];

/**
 * Tab ids that existed before the redesign. Old links (bookmarks, the health
 * panel, Slack threads) keep landing somewhere sensible: the Support tab's
 * actions now live in the header and the Danger zone, so it opens Overview;
 * Branding merged into Updates.
 */
const LEGACY_TAB: Record<string, DeviceTabId> = { support: 'overview', branding: 'updates' };

/** Narrows an arbitrary `?tab=` value; anything unknown falls back to Overview. */
export function toDeviceTabId(raw: string | null | undefined): DeviceTabId {
  if (raw && LEGACY_TAB[raw]) return LEGACY_TAB[raw];
  return (DEVICE_TAB_IDS as readonly string[]).includes(raw || '')
    ? (raw as DeviceTabId)
    : 'overview';
}

/**
 * What every tab receives. Identical for every tab on purpose: a tab that needs
 * something else asks for it here, so the addition is visible to the others
 * rather than smuggled in through one tab's own props.
 */
export interface DeviceTabProps {
  /** The merged row — device check-in + hardware unit + resolved target. */
  row: FleetRow;
  /** The catalogue model, which carries the photo. Absent when not loaded. */
  model?: DeviceModelRow | null;
  /** Re-read the page's data. Safe to call from any tab, after any write. */
  onRefresh: () => void;
  /** Switch the page to another tab (Overview's "All issues", "All history"…). */
  goTo?: (tab: DeviceTabId) => void;
  /**
   * A capture the header just requested (Collect logs / Screenshot), so the
   * Logs tab can show it as "Waiting for till" before its own read catches up.
   */
  freshCommand?: FleetCommand | null;
  /**
   * The page's one read of this till's commands (newest first) — Overview's
   * recent activity and hardware card read it rather than fetching again.
   */
  commands?: FleetCommand[];
  /**
   * Open Issue Console problems for this till (7 days). `undefined` = still
   * loading, `null` = the read failed — never an empty list on a failure.
   */
  openIssues?: DeviceHealthProblem[] | null;
}

/**
 * THE BOUNDARY BETWEEN THIS PAGE AND THE INVENTORY UNIT PAGE.
 *
 * The unit page owns the HARDWARE life — order, allocation, provisioning
 * checklist, condition, damage, journey — and already renders both
 * `TimelinePanel` and `JourneyTimeline` for it. This page owns the SOFTWARE
 * life: installs, versions, health, support actions, branding.
 *
 * Neither repeats the other. Two timelines over one device would have to be
 * kept in agreement, and they would drift.
 */
export function unitHref(unitId: string | undefined): string | null {
  return unitId ? `/inventory/units/${unitId}` : null;
}

/**
 * This page's own URL. The fleet table and the drawer both link here.
 *
 * `deviceType` is carried in the query rather than the path because a device id
 * is only unique WITHIN a type — the same rule `fleetTargetKey` enforces. A
 * link that drops it still opens the device (the page defaults), but a second
 * device type would resolve the wrong row without it, so every caller that
 * knows the type passes it.
 */
export const LEGACY_DEVICE_BASE = '/pos-config/fleet';

/** `basePath` lets Fleet Monitoring link to its own device route (see deviceLinkBase.tsx). */
export function deviceHref(deviceId: string, tab?: DeviceTabId, deviceType?: string, basePath: string = LEGACY_DEVICE_BASE): string {
  const q = new URLSearchParams();
  if (tab) q.set('tab', tab);
  if (deviceType) q.set('type', deviceType);
  const s = q.toString();
  return `${basePath}/${encodeURIComponent(deviceId)}${s ? `?${s}` : ''}`;
}

/**
 * "2 min ago" / "3 days ago", or `null` when there is no timestamp.
 *
 * NULL IS NOT "NEVER". A missing timestamp means we were not told, and the
 * caller renders an em dash for it — writing "never" here would state a fact
 * about the device that we only have about our own records.
 */
export function relativeTime(ts: number | null | undefined, now: number = Date.now()): string | null {
  if (ts == null || !Number.isFinite(ts) || ts <= 0) return null;
  const s = Math.floor((now - ts) / 1000);
  if (s < 0) return 'just now';           // a clock ahead of ours is not the future
  if (s < 60) return `${s || 1} sec ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? '' : 's'} ago`;
  return `${Math.floor(mo / 12)} year${Math.floor(mo / 12) === 1 ? '' : 's'} ago`;
}

/** `24 Sep 2026, 22:21`, or null when there is no timestamp. */
export function formatWhen(ts: number | null | undefined): string | null {
  if (ts == null || !Number.isFinite(ts) || ts <= 0) return null;
  return new Date(ts).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** `24 Sep 2026`, for a date that does not need a time beside it. */
export function formatDay(ts: number | null | undefined): string | null {
  if (ts == null || !Number.isFinite(ts) || ts <= 0) return null;
  return new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Is this device reporting in?
 *
 * The same threshold `classifyDeviceRelease` uses for `stale`, so the header's
 * "Online" light and the release status can never disagree about one device.
 */
export const ONLINE_WITHIN_MS = 15 * 60 * 1000;

export function isOnline(row: FleetRow, now: number = Date.now()): boolean {
  const seen = row.updatedAt ?? null;
  return seen != null && now - seen <= ONLINE_WITHIN_MS;
}

/** The model's photo, preferring the hero and falling back to the first gallery shot. */
export function modelImage(model: DeviceModelRow | null | undefined): string | undefined {
  const media = (model as any)?.media;
  return media?.heroImageUrl || media?.photos?.[0] || undefined;
}

/**
 * What to call this device in a heading — the same words the Fleet Monitoring
 * list uses (`deviceDisplayName`), so a row and the page it opens never name
 * one till two ways. `model` is accepted for call-site compatibility only: the
 * long catalogue title belongs on the small grey line, never the heading.
 */
export function deviceTitle(row: FleetRow, _model?: DeviceModelRow | null): string {
  return deviceDisplayName(row);
}
